import { openDB } from 'idb'

// Encryption-at-rest for the EMR IndexedDB.
//
// Records are encrypted with AES-GCM-256 using a NON-EXTRACTABLE WebCrypto key.
// The key lives in its own IndexedDB database ('erm-keys-database') as a CryptoKey
// object — it can be used by this origin's code but its raw bytes can never be
// exported, so DB dumps / disk inspection only yield ciphertext.
//
// On http origins (no crypto.subtle outside secure contexts) the layer degrades
// to plaintext passthrough so local development keeps working; production runs
// on https where encryption is always active.

const KEYS_DB_NAME = 'erm-keys-database'
const KEYS_STORE_NAME = 'keys'
const PRIMARY_KEY_ID = 'erm-data-key-v1'

let cachedKeyPromise = null

export function isIdbEncryptionSupported() {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext === true &&
    typeof crypto !== 'undefined' &&
    Boolean(crypto?.subtle)
  )
}

async function openKeysDb() {
  return openDB(KEYS_DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(KEYS_STORE_NAME)) {
        db.createObjectStore(KEYS_STORE_NAME)
      }
    },
  })
}

export async function getOrCreateIdbCryptoKey() {
  if (!isIdbEncryptionSupported()) {
    return null
  }
  if (!cachedKeyPromise) {
    cachedKeyPromise = (async () => {
      const db = await openKeysDb()
      const existingKey = await db.get(KEYS_STORE_NAME, PRIMARY_KEY_ID)
      if (existingKey) {
        return existingKey
      }
      const newKey = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        false, // non-extractable: raw key material can never leave the browser
        ['encrypt', 'decrypt'],
      )
      await db.put(KEYS_STORE_NAME, newKey, PRIMARY_KEY_ID)
      return newKey
    })().catch((error) => {
      // if key handling fails we must not break the EMR flows — fall back to plaintext
      console.error('IDB crypto key unavailable, storing records unencrypted', error)
      cachedKeyPromise = null
      return null
    })
  }
  return cachedKeyPromise
}

/**
 * Encrypts a record for storage. Returns { keyValue, __enc: { iv, data } } where only
 * the primary key stays readable (needed for IndexedDB upserts); everything else is
 * AES-GCM ciphertext. Falls back to the original record when encryption is unavailable.
 */
export async function encryptIdbRecord(record, keyPath = 'id') {
  const key = await getOrCreateIdbCryptoKey()
  if (!key) {
    return record
  }
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(JSON.stringify(record))
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
  return {
    [keyPath]: record?.[keyPath],
    __enc: { iv, data },
  }
}

/**
 * Decrypts a record read from storage. Plaintext (legacy / fallback) records pass
 * through unchanged, so readers never need to care how a record was stored.
 */
export async function decryptIdbRecord(storedRecord) {
  if (!storedRecord || !storedRecord.__enc) {
    return storedRecord
  }
  try {
    const key = await getOrCreateIdbCryptoKey()
    if (!key) {
      return null
    }
    const { iv, data } = storedRecord.__enc
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)
    return JSON.parse(new TextDecoder().decode(decrypted))
  } catch (error) {
    // wrong/rotated key (e.g. keys DB cleared but data DB kept) — treat as cache miss
    console.warn('Could not decrypt IDB record, ignoring it', error)
    return null
  }
}

export async function decryptIdbRecords(storedRecords) {
  const decrypted = await Promise.all((storedRecords || []).map(decryptIdbRecord))
  return decrypted.filter(Boolean)
}
